import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Search, ChevronDown, X, Plus, Trash2, Save, ArrowLeft,
    Tag, MapPin, DollarSign, PercentIcon, Clock, AlertCircle,
    Building2, Globe, Calculator, Settings, Shield
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { RootState } from "../../redux/store";
import { fetchCountries, fetchRegionsByCountry } from "../../redux/Slices/LocationSlice";
import CategoryDropdown from "../../components/ui/CategoryDropdown";
import AdminRegionDropdown from "../../components/ui/AdminRegionDropdown";
import {
    createQuotePricing,
    updateQuotePricing,
    bulkCreateQuotePricingRegions,
    bulkUpdateQuotePricingRegions,
    deleteQuotePricingRegion
} from "../../redux/Slices/AdminQuotePricingSlice";
import type {
    QuotePricingModel,
    QuotePricingRegion,
    QuotePricingCategoryRef,
    QuotePricingRegionRef
} from "../../modules/shared/model/admin-quote-pricing.interfaces";
import { QuotePricingStatusEnum } from "../../modules/shared/model/admin-quote-pricing.enum";

interface QuotePricingFormProps {
    onClose: () => void;
    onSave: (data: any) => void;
    editData?: QuotePricingModel | null;
}

interface Category {
    id: string;
    name: string;
    subcategories?: string[];
}

interface Region {
    id: string;
    name: string;
    country?: string;
    state?: string;
    city?: string;
}


// Dropdown component for subcategories
const SubcategoryDropdown = ({ selectedSubcategory, onSubcategorySelect, category, placeholder = "Select subcategory..." }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const dropdownRef = useRef<HTMLDivElement>(null);

    const subcategories = category?.subcategories || [];
    const filteredSubcategories = subcategories.filter(subcategory =>
        subcategory.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleSubcategorySelect = (subcategory: string) => {
        onSubcategorySelect(subcategory);
        setIsOpen(false);
        setSearchQuery("");
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    if (!category) {
        return (
            <div className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-500">
                Select a category first
            </div>
        );
    }

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between px-3 py-2 border rounded-lg min-h-[42px] bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    {selectedSubcategory ? (
                        <>
                            <Tag className="h-4 w-4 text-gray-500 flex-shrink-0" />
                            <span className="truncate">{selectedSubcategory}</span>
                        </>
                    ) : (
                        <span className="text-gray-500">{placeholder}</span>
                    )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    {selectedSubcategory && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onSubcategorySelect("");
                            }}
                            className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                        >
                            <X className="h-3 w-3 text-gray-400" />
                        </button>
                    )}
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </div>
            </button>

            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden">
                    <div className="p-3 border-b border-gray-200">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                                type="text"
                                placeholder="Search subcategories..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                    </div>

                    <div className="max-h-60 overflow-y-auto">
                        {filteredSubcategories.map((subcategory, index) => (
                            <button
                                key={index}
                                type="button"
                                onClick={() => handleSubcategorySelect(subcategory)}
                                className={`w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-gray-50 transition-colors ${selectedSubcategory === subcategory ? "bg-blue-50 border-l-4 border-blue-500" : ""
                                    }`}
                            >
                                <div className="flex-shrink-0">
                                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                                        <Tag className="h-4 w-4 text-gray-500" />
                                    </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 truncate">{subcategory}</p>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};


// Switch toggle component
const SwitchToggle = ({ checked, onChange, label, description }) => (
    <div className="flex items-center justify-between">
        <div className="flex-1">
            <label className="text-sm font-medium text-gray-700">{label}</label>
            {description && (
                <p className="text-xs text-gray-500 mt-1">{description}</p>
            )}
        </div>
        <button
            type="button"
            onClick={() => onChange(!checked)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? "bg-blue-600" : "bg-gray-200"
                }`}
        >
            <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? "translate-x-6" : "translate-x-1"
                    }`}
            />
        </button>
    </div>
);

// Status dropdown component
const StatusDropdown = ({ selectedStatus, onStatusSelect, placeholder = "Select status..." }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const statusOptions = [
        { value: 1, label: "Active", color: "bg-green-100 text-green-700" },
        { value: 2, label: "Inactive", color: "bg-gray-100 text-gray-700" },
        { value: 3, label: "Draft", color: "bg-yellow-100 text-yellow-700" },
        { value: 4, label: "Archived", color: "bg-red-100 text-red-700" }
    ];

    const selectedOption = statusOptions.find(option => option.value === selectedStatus);

    const handleStatusSelect = (status: number) => {
        onStatusSelect(status);
        setIsOpen(false);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between px-3 py-2 border rounded-lg min-h-[42px] bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    {selectedOption ? (
                        <>
                            <Badge variant={undefined} className={selectedOption.color}>
                                {selectedOption.label}
                            </Badge>
                        </>
                    ) : (
                        <span className="text-gray-500">{placeholder}</span>
                    )}
                </div>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>

            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                    {statusOptions.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => handleStatusSelect(option.value)}
                            className={`w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-gray-50 transition-colors ${selectedStatus === option.value ? "bg-blue-50 border-l-4 border-blue-500" : ""
                                }`}
                        >
                            <Badge variant={undefined} className={option.color}>
                                {option.label}
                            </Badge>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const QuotePricingForm: React.FC<QuotePricingFormProps> = ({ onClose, onSave, editData }) => {
    const dispatch = useDispatch();

    // Form state
    const [formData, setFormData] = useState({
        // Basic settings
        requireDeposit: false,
        allowNegotiation: false,
        autoAdjustment: false,
        minimumFee: false,
        status: 1 as QuotePricingStatusEnum,

        // Category settings
        category: null as Category | null,
        subcategory: "",

        // Regions
        regions: [] as Array<{
            id: string;
            region: Region | null;
            subcategory: string;
            baseQuotePrice: number;
            emergencyQuotePrice: number;
            regularCommission: number;
            regularDayCommission: number;
            regularNightCommission: number;
            regularHolidayCommission: number;
            distanceModifier: boolean;
            nightModifier: boolean;
            holidayModifier: boolean;
            emergencyModifier: boolean;
        }>
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [existingRegions, setExistingRegions] = useState<any[]>([]);
    const [deletingRegions, setDeletingRegions] = useState(false);

    // Initialize form data if editing
    useEffect(() => {
        if (editData) {
            console.log("Edit data:", editData);
            setFormData({
                requireDeposit: editData.requireDeposit || false,
                allowNegotiation: editData.allowNegotiation || false,
                autoAdjustment: editData.autoAdjustment || false,
                minimumFee: editData.minimumFee || false,
                status: editData.status?.id || 1,
                category: editData.category || null,
                subcategory: "",
                regions: []
            });

            // Use regions from the plan data
            if (editData.regions && editData.regions.length > 0) {
                setExistingRegions(editData.regions);

                // Convert regions to form sections format
                const sections = editData.regions.map((region, index) => ({
                    id: region.id || `existing_${index}`,
                    region: region.region || null,
                    subcategory: region.subcategory || "",
                    baseQuotePrice: region.baseQuotePrice ,
                    emergencyQuotePrice: region.emergencyQuotePrice ,
                    regularCommission: region.regularCommission ,
                    regularDayCommission: region.regularDayCommission ,
                    regularNightCommission: region.regularNightCommission ,
                    regularHolidayCommission: region.regularHolidayCommission ,
                    distanceModifier: region.distanceModifier || false,
                    nightModifier: region.nightModifier || false,
                    holidayModifier: region.holidayModifier || false,
                    emergencyModifier: region.emergencyModifier || false,
                }));

                console.log("Setting form regions:", sections);
                setFormData(prev => ({
                    ...prev,
                    regions: sections as any
                }));
            }
        } else {
            // Reset form for new plan
            setFormData({
                requireDeposit: false,
                allowNegotiation: false,
                autoAdjustment: false,
                minimumFee: false,
                status: 1,
                category: null,
                subcategory: "",
                regions: []
            });
            setExistingRegions([]);
            setDeletingRegions(false);
        }
    }, [editData]);

    // Load regions on component mount
    useEffect(() => {
        dispatch(fetchCountries({ page: 1, limit: 10 }) as any);
    }, [dispatch]);

    // Handle form field changes
    const handleFieldChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    // Handle region changes
    const handleRegionChange = (index: number, field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            regions: prev.regions.map((region, i) =>
                i === index ? { ...region, [field]: value } : region
            )
        }));
    };

    // Add new region
    const addRegion = () => {
        setFormData(prev => ({
            ...prev,
            regions: [...prev.regions, {
                id: Math.random().toString(),
                region: null,
                subcategory: prev.subcategory,
                baseQuotePrice: 0,
                emergencyQuotePrice: 0,
                regularCommission: 0,
                regularDayCommission: 0,
                regularNightCommission: 0,
                regularHolidayCommission: 0,
                distanceModifier: false,
                nightModifier: false,
                holidayModifier: false,
                emergencyModifier: false,
            }]
        }));
    };

    // Remove region
    const removeRegion = (index: number) => {
        setFormData(prev => ({
            ...prev,
            regions: prev.regions.filter((_, i) => i !== index)
        }));
    };

    // Helper function to get regions that will be deleted
    const getRegionsToDelete = () => {
        if (!editData || !existingRegions.length) return [];

        const currentRegionIds = formData.regions
            .filter(region => region.id.startsWith("existing_") || existingRegions.some(existingRegion => existingRegion.id === region.id))
            .map(region => region.id.startsWith("existing_")
                ? existingRegions[parseInt(region.id.split("_")[1])]?.id
                : region.id
            )
            .filter(Boolean);

        return existingRegions.filter(region => !currentRegionIds.includes(region.id));
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const pricingData = {
                requireDeposit: formData.requireDeposit,
                allowNegotiation: formData.allowNegotiation,
                autoAdjustment: formData.autoAdjustment,
                minimumFee: formData.minimumFee,
                status: {
                    id: Number(formData.status),
                    name: QuotePricingStatusEnum[formData.status]
                },
                category: formData.category ? {
                    id: formData.category.id,
                    name: formData.category.name
                } : undefined
            };

            console.log("Pricing data being submitted:", pricingData);

            let pricingId: string;

            if (editData) {
                // Update existing pricing
                const updatedPricing = await dispatch(updateQuotePricing({
                    id: editData.id,
                    data: pricingData as any
                }) as any).unwrap();
                pricingId = updatedPricing.id;

                // Identify regions to delete (existing regions not in current form)
                const currentRegionIds = formData.regions
                    .filter(region => region.id.startsWith("existing_") || existingRegions.some(existingRegion => existingRegion.id === region.id))
                    .map(region => region.id.startsWith("existing_")
                        ? existingRegions[parseInt(region.id.split("_")[1])]?.id
                        : region.id
                    )
                    .filter(Boolean);

                const regionsToDelete = existingRegions
                    .filter(region => !currentRegionIds.includes(region.id))
                    .map(region => region.id);

                // Delete removed regions
                if (regionsToDelete.length > 0) {
                    console.log("Deleting regions:", regionsToDelete);
                    setDeletingRegions(true);
                    try {
                        // Delete all regions in parallel for better performance
                        await Promise.all(
                            regionsToDelete.map(regionId =>
                                dispatch(deleteQuotePricingRegion(regionId) as any).unwrap()
                            )
                        );
                        console.log(`Successfully deleted ${regionsToDelete.length} region(s)`);
                    } catch (error) {
                        console.error("Failed to delete some regions:", error);
                        // Continue with the rest of the operation even if some deletions fail
                    } finally {
                        setDeletingRegions(false);
                    }
                }

                // Handle regions update using bulk update API
                const updates = formData.regions
                    .filter(region => region.id.startsWith("existing_") || existingRegions.some(existingRegion => existingRegion.id === region.id))
                    .map(region => ({
                        id: region.id.startsWith("existing_")
                            ? existingRegions[parseInt(region.id.split("_")[1])]?.id
                            : region.id,
                        data: {
                            quotePricing: {
                                id: pricingId,
                                name: formData.category?.name || "Quote Pricing"
                            },
                            region: region.region ? {
                                id: region.region.id,
                                name: region.region.name
                            } : undefined,
                            subcategory: region.subcategory,
                            baseQuotePrice: region.baseQuotePrice,
                            emergencyQuotePrice: region.emergencyQuotePrice,
                            regularCommission: region.regularCommission,
                            regularDayCommission: region.regularDayCommission,
                            regularNightCommission: region.regularNightCommission,
                            regularHolidayCommission: region.regularHolidayCommission,
                            distanceModifier: region.distanceModifier,
                            nightModifier: region.nightModifier,
                            holidayModifier: region.holidayModifier,
                            emergencyModifier: region.emergencyModifier
                        }
                    }))
                    .filter(update => update.id); // Only include valid updates

                if (updates.length > 0) {
                    await dispatch(bulkUpdateQuotePricingRegions({ items: updates } as any) as any).unwrap();
                }

                // Handle new regions (those without existing IDs)
                const newRegions = formData.regions.filter(region =>
                    !region.id.startsWith("existing_") &&
                    !existingRegions.some(existingRegion => existingRegion.id === region.id)
                );

                if (newRegions.length > 0) {
                    const newRegionsData = newRegions.map(region => ({
                        quotePricing: {
                            id: pricingId,
                            name: formData.category?.name || "Quote Pricing"
                        },
                        region: region.region ? {
                            id: region.region.id,
                            name: region.region.name
                        } : undefined,
                        subcategory: region.subcategory,
                        baseQuotePrice: region.baseQuotePrice,
                        emergencyQuotePrice: region.emergencyQuotePrice,
                        regularCommission: region.regularCommission,
                        regularDayCommission: region.regularDayCommission,
                        regularNightCommission: region.regularNightCommission,
                        regularHolidayCommission: region.regularHolidayCommission,
                        distanceModifier: region.distanceModifier,
                        nightModifier: region.nightModifier,
                        holidayModifier: region.holidayModifier,
                        emergencyModifier: region.emergencyModifier
                    }));

                    console.log("New regions data being submitted:", newRegionsData);

                    await dispatch(bulkCreateQuotePricingRegions({ items: newRegionsData } as any) as any).unwrap();
                }
            } else {
                // Create new pricing
                const createdPricing = await dispatch(createQuotePricing(pricingData as any) as any).unwrap();
                pricingId = createdPricing.id;

                // Create pricing regions only if region is provided
                const validRegions = formData.regions.filter(region =>
                    region.region && region.baseQuotePrice > 0
                );

                if (validRegions.length > 0) {
                    const regionsData = validRegions.map(region => ({
                        quotePricing: {
                            id: pricingId,
                            name: formData.category?.name || "Quote Pricing"
                        },
                        region: {
                            id: region.region!.id,
                            name: region.region!.name
                        },
                        subcategory: region.subcategory,
                        baseQuotePrice: region.baseQuotePrice,
                        emergencyQuotePrice: region.emergencyQuotePrice,
                        regularCommission: region.regularCommission,
                        regularDayCommission: region.regularDayCommission,
                        regularNightCommission: region.regularNightCommission,
                        regularHolidayCommission: region.regularHolidayCommission,
                        distanceModifier: region.distanceModifier,
                        nightModifier: region.nightModifier,
                        holidayModifier: region.holidayModifier,
                        emergencyModifier: region.emergencyModifier
                    }));

                    console.log("Regions data being submitted:", regionsData);

                    await dispatch(bulkCreateQuotePricingRegions({ items: regionsData } as any) as any).unwrap();
                }
            }

            // Success
            onSave(pricingData);
            onClose();

            // Reset form
            setFormData({
                requireDeposit: false,
                allowNegotiation: false,
                autoAdjustment: false,
                minimumFee: false,
                status: 1,
                category: null,
                subcategory: "",
                regions: []
            });
            setExistingRegions([]);
            setDeletingRegions(false);

        } catch (error) {
            console.error("Error saving quote pricing:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </button>
                        <div>
                            <h2 className="text-xl font-semibold">
                                {editData ? "Edit Quote Pricing" : "Add Quote Pricing"}
                            </h2>
                            <p className="text-gray-600 text-sm">
                                {editData ? "Update pricing rules and commissions" : "Create new pricing rules and commissions"}
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={isSubmitting || deletingRegions}
                            className="flex items-center gap-2"
                        >
                            {isSubmitting || deletingRegions ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    {deletingRegions ? "Deleting Regions..." :
                                        editData ? "Updating..." : "Creating..."}
                                </>
                            ) : (
                                <>
                                    <Save className="h-4 w-4" />
                                    {editData ? "Update Pricing" : "Create Pricing"}
                                </>
                            )}
                        </Button>
                    </div>
                </div>

                {/* Form Content */}
                <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                    <div className="space-y-8">
                        {/* Basic Settings */}
                        <Card className="p-6">
                            <div className="flex items-center gap-2 mb-6">
                                <Settings className="h-5 w-5 text-blue-600" />
                                <h3 className="text-lg font-semibold">Basic Settings</h3>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <SwitchToggle
                                        checked={formData.requireDeposit}
                                        onChange={(value) => handleFieldChange("requireDeposit", value)}
                                        label="Require Deposit"
                                        description="Require a deposit before starting work"
                                    />

                                    <SwitchToggle
                                        checked={formData.allowNegotiation}
                                        onChange={(value) => handleFieldChange("allowNegotiation", value)}
                                        label="Allow Negotiation"
                                        description="Allow price negotiation between parties"
                                    />
                                </div>

                                <div className="space-y-4">
                                    <SwitchToggle
                                        checked={formData.autoAdjustment}
                                        onChange={(value) => handleFieldChange("autoAdjustment", value)}
                                        label="Auto Adjustment"
                                        description="Automatically adjust prices based on market conditions"
                                    />

                                    <SwitchToggle
                                        checked={formData.minimumFee}
                                        onChange={(value) => handleFieldChange("minimumFee", value)}
                                        label="Minimum Fee"
                                        description="Enforce a minimum fee for all quotes"
                                    />
                                </div>
                            </div>
                        </Card>

                        {/* Status Settings */}
                        <Card className="p-6">
                            <div className="flex items-center gap-2 mb-6">
                                <Shield className="h-5 w-5 text-purple-600" />
                                <h3 className="text-lg font-semibold">Status Settings</h3>
                            </div>

                            <div className="max-w-md">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Status *
                                </label>
                                <StatusDropdown
                                    selectedStatus={formData.status}
                                    onStatusSelect={(status) => handleFieldChange("status", status)}
                                />
                            </div>
                        </Card>

                        {/* Category Settings */}
                        <Card className="p-6">
                            <div className="flex items-center gap-2 mb-6">
                                <Tag className="h-5 w-5 text-green-600" />
                                <h3 className="text-lg font-semibold">Category Settings</h3>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Category *
                                    </label>
                                    <CategoryDropdown
                                        selectedCategory={formData.category}
                                        onCategorySelect={(category) => {
                                            handleFieldChange("category", category);
                                            handleFieldChange("subcategory", "");
                                        }}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Subcategory *
                                    </label>
                                    <SubcategoryDropdown
                                        selectedSubcategory={formData.subcategory}
                                        onSubcategorySelect={(subcategory) => handleFieldChange("subcategory", subcategory)}
                                        category={formData.category}
                                    />
                                </div>
                            </div>
                        </Card>



                        {/* Regions */}
                        <Card className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-2">
                                    <Globe className="h-5 w-5 text-orange-600" />
                                    <h3 className="text-lg font-semibold">Regional Pricing</h3>
                                </div>
                                <Button
                                    type="button"
                                    onClick={addRegion}
                                    className="flex items-center gap-2"
                                >
                                    <Plus className="h-4 w-4" />
                                    Add Region
                                </Button>
                            </div>

                            {/* Regions to be deleted warning */}
                            {editData && getRegionsToDelete().length > 0 && (
                                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                    <div className="flex items-center gap-2 text-yellow-700">
                                        <AlertCircle className="h-4 w-4" />
                                        <span className="text-sm font-medium">
                                            {getRegionsToDelete().length === 1
                                                ? "1 region will be removed"
                                                : `${getRegionsToDelete().length} regions will be removed`
                                            } when you save this pricing.
                                        </span>
                                    </div>
                                </div>
                            )}

                            {formData.regions.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <Globe className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                                    <p>No regions added yet</p>
                                    <p className="text-sm">Click Add Region to get started</p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {formData.regions.map((region, index) => (
                                        <Card key={region.id} className="p-6 bg-gray-50">
                                            <div className="flex items-center justify-between mb-4">
                                                <h4 className="font-medium">Region {index + 1}</h4>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => removeRegion(index)}
                                                    className="text-red-600 hover:text-red-700"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                {/* Region Selection */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Region *
                                                    </label>
                                                    <AdminRegionDropdown
                                                        selectedRegion={region.region}
                                                        onRegionSelect={(selectedRegion) =>
                                                            handleRegionChange(index, "region", selectedRegion)
                                                        }
                                                    />
                                                </div>

                                                {/* Subcategory */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Subcategory
                                                    </label>
                                                    <Input
                                                        type="text"
                                                        value={region.subcategory}
                                                        onChange={(e) => handleRegionChange(index, "subcategory", e.target.value)}
                                                        placeholder="Enter subcategory"
                                                    />
                                                </div>
                                            </div>

                                            {/* Pricing */}
                                            <div className="mt-6">
                                                <h5 className="font-medium mb-4 flex items-center gap-2">
                                                    <DollarSign className="h-4 w-4" />
                                                    Pricing
                                                </h5>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Base Quote Price *
                                                        </label>
                                                        <Input
                                                            type="number"
                                                            value={region.baseQuotePrice}
                                                            onChange={(e) => handleRegionChange(index, "baseQuotePrice", parseFloat(e.target.value) )}
                                                            placeholder="0.00"
                                                            min="0"
                                                            step="0.01"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Emergency Quote Price
                                                        </label>
                                                        <Input
                                                            type="number"
                                                            value={region.emergencyQuotePrice}
                                                            onChange={(e) => handleRegionChange(index, "emergencyQuotePrice", parseFloat(e.target.value) )}
                                                            placeholder="0.00"
                                                            min="0"
                                                            step="0.01"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Commission Settings */}
                                            <div className="mt-6">
                                                <h5 className="font-medium mb-4 flex items-center gap-2">
                                                    <PercentIcon className="h-4 w-4" />
                                                    Commission Rates
                                                </h5>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Emergency Commission *
                                                        </label>
                                                        <Input
                                                            type="number"
                                                            value={region.regularCommission}
                                                            onChange={(e) => handleRegionChange(index, "regularCommission", parseFloat(e.target.value) )}
                                                            
                                                            min="0"
                                                            max="100"
                                                            step="0.01"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Day Commission
                                                        </label>
                                                        <Input
                                                            type="number"
                                                            value={region.regularDayCommission}
                                                            onChange={(e) => handleRegionChange(index, "regularDayCommission", parseFloat(e.target.value) )}
                                                            
                                                            min="0"
                                                            max="100"
                                                            step="0.01"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Night Commission
                                                        </label>
                                                        <Input
                                                            type="number"
                                                            value={region.regularNightCommission}
                                                            onChange={(e) => handleRegionChange(index, "regularNightCommission", parseFloat(e.target.value) )}
                                                            
                                                            min="0"
                                                            max="100"
                                                            step="0.01"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Holiday Commission
                                                        </label>
                                                        <Input
                                                            type="number"
                                                            value={region.regularHolidayCommission}
                                                            onChange={(e) => handleRegionChange(index, "regularHolidayCommission", parseFloat(e.target.value) )}
                                                            
                                                            min="0"
                                                            max="100"
                                                            step="0.01"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Modifiers */}
                                            <div className="mt-6">
                                                <h5 className="font-medium mb-4 flex items-center gap-2">
                                                    <Clock className="h-4 w-4" />
                                                    Modifiers
                                                </h5>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <SwitchToggle
                                                        checked={region.distanceModifier}
                                                        onChange={(value) => handleRegionChange(index, "distanceModifier", value)}
                                                        label="Distance Modifier"
                                                        description="Apply distance-based pricing adjustments"
                                                    />

                                                    <SwitchToggle
                                                        checked={region.nightModifier}
                                                        onChange={(value) => handleRegionChange(index, "nightModifier", value)}
                                                        label="Night Modifier"
                                                        description="Apply night-time pricing adjustments"
                                                    />

                                                    <SwitchToggle
                                                        checked={region.holidayModifier}
                                                        onChange={(value) => handleRegionChange(index, "holidayModifier", value)}
                                                        label="Holiday Modifier"
                                                        description="Apply holiday pricing adjustments"
                                                    />

                                                    <SwitchToggle
                                                        checked={region.emergencyModifier}
                                                        onChange={(value) => handleRegionChange(index, "emergencyModifier", value)}
                                                        label="Emergency Modifier"
                                                        description="Apply emergency pricing adjustments"
                                                    />
                                                </div>
                                            </div>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </Card>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default QuotePricingForm;
